/**
 * InputGroupMobile.tsx
 *
 * Mobile-optimized version of InputGroup.
 * Key differences:
 * - Date and time inputs are side-by-side in a row
 * - Slightly more compact layout
 */

import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { debounce } from "lodash";
import { API_CONFIG } from "./appConfig";
import { getConfigSettings } from "./configUtils";
import debug from "./debug";

interface InputGroupMobileProps {
  apiKey: string;
  onFromLocationChange: (
    location: google.maps.LatLngLiteral | null,
    address?: string,
  ) => void;
  onToLocationChange: (
    location: google.maps.LatLngLiteral | null,
    address?: string,
  ) => void;
  selectedDate: string;
  onDateChange: (date: string) => void;
  selectedTime: string;
  onTimeChange: (time: string) => void;
}

interface LocationInputProps {
  type: "from" | "to";
  inputRef: React.RefObject<HTMLInputElement | null>;
  suggestions: { text: string; location?: google.maps.LatLngLiteral }[];
  isLoading: boolean;
  isSearching: boolean;
  isDropdownOpen: boolean;
  highlightedIndex: number;
  onInputChange: (value: string) => void;
  onClear: () => void;
  onUseMyLocation: () => void;
  onLocationChange: (
    location: google.maps.LatLngLiteral | null,
    address?: string,
  ) => void;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onHighlight: (index: number) => void;
  onSelect: (index: number) => void;
}

interface TrpSettings {
  apiKey?: string;
  mapsAPIUrlProd?: string;
  geocodeAPIUrlProd?: string;
}

const LocationInput: React.FC<LocationInputProps> = ({
  type,
  inputRef,
  suggestions,
  isLoading,
  isSearching,
  isDropdownOpen,
  highlightedIndex,
  onInputChange,
  onClear,
  onUseMyLocation,
  onBlur,
  onFocus,
  onKeyDown,
  onHighlight,
  onSelect,
}) => (
  <div className="input-group">
    <div className="content-column">
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          placeholder={
            type === "from" ? "Starting location" : "Destination"
          }
          className="input"
          onChange={(e) => onInputChange(e.target.value)}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          aria-label={type === "from" ? "Starting location" : "Destination"}
        />
        {isDropdownOpen &&
          (isSearching ? (
            <div className="suggestions-loading">Searching...</div>
          ) : suggestions.length > 0 ? (
            <ul className="suggestions" role="listbox">
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  tabIndex={0}
                  className={`suggestion-item ${index === highlightedIndex ? "suggestion-item-highlighted" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(index);
                  }}
                  onTouchStart={(e) => {
                    // Better touch handling for mobile
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    onSelect(index);
                  }}
                  onMouseEnter={() => onHighlight(index)}
                  onFocus={() => onHighlight(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(index);
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      const next = e.currentTarget
                        .nextElementSibling as HTMLElement;
                      if (next) next.focus();
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      const prev = e.currentTarget
                        .previousElementSibling as HTMLElement;
                      if (prev) {
                        prev.focus();
                      } else {
                        inputRef.current?.focus();
                      }
                    } else if (e.key === "Escape") {
                      inputRef.current?.focus();
                    }
                  }}
                  role="option"
                  aria-selected={index === highlightedIndex}
                >
                  {suggestion.text}
                </li>
              ))}
            </ul>
          ) : null)}
        {inputRef.current?.value && (
          <button
            onClick={onClear}
            className="clear-button"
            aria-label="Clear input"
            type="button"
          >
            ✕
          </button>
        )}
      </div>
      <button
        onClick={onUseMyLocation}
        disabled={isLoading}
        className={`location-button ${isLoading ? "disabled" : ""}`}
        aria-label={isLoading ? "Loading location" : "Use my location"}
        type="button"
      >
        {isLoading ? "Loading..." : "Use My Location"}
      </button>
    </div>
  </div>
);

const InputGroupMobile: React.FC<InputGroupMobileProps> = ({
  apiKey,
  onFromLocationChange,
  onToLocationChange,
  selectedDate,
  onDateChange,
  selectedTime,
  onTimeChange,
}) => {
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const [fromSuggestions, setFromSuggestions] = useState<
    { text: string; location?: google.maps.LatLngLiteral }[]
  >([]);
  const [toSuggestions, setToSuggestions] = useState<
    { text: string; location?: google.maps.LatLngLiteral }[]
  >([]);
  const [isLoadingFrom, setIsLoadingFrom] = useState<boolean>(false);
  const [isLoadingTo, setIsLoadingTo] = useState<boolean>(false);
  const [isSearchingFrom, setIsSearchingFrom] = useState<boolean>(false);
  const [isSearchingTo, setIsSearchingTo] = useState<boolean>(false);
  const [isFromDropdownOpen, setIsFromDropdownOpen] = useState<boolean>(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState<boolean>(false);
  const [fromHighlightedIndex, setFromHighlightedIndex] = useState<number>(-1);
  const [toHighlightedIndex, setToHighlightedIndex] = useState<number>(-1);
  const { autocompleteCenter, autocompleteRadius, regionCorners } =
    getConfigSettings();

  // AbortController refs for cancelling in-flight requests
  const fromAbortRef = useRef<AbortController | null>(null);
  const toAbortRef = useRef<AbortController | null>(null);

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      fromAbortRef.current?.abort();
      toAbortRef.current?.abort();
    };
  }, []);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setFromHighlightedIndex(-1);
  }, [fromSuggestions]);

  useEffect(() => {
    setToHighlightedIndex(-1);
  }, [toSuggestions]);

  const performSearch = useCallback(
    async (value: string, type: "from" | "to", signal: AbortSignal) => {
      const setSuggestions =
        type === "from" ? setFromSuggestions : setToSuggestions;
      const setIsSearching =
        type === "from" ? setIsSearchingFrom : setIsSearchingTo;
      const setIsDropdownOpen =
        type === "from" ? setIsFromDropdownOpen : setIsToDropdownOpen;

      if (!value) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      setIsDropdownOpen(true);

      const settings: TrpSettings = window.trpSettings || {};
      const isDevMode = process.env.NODE_ENV === "development";
      const mapsAPIUrl = isDevMode
        ? API_CONFIG.PLACES_AUTOCOMPLETE
        : settings.mapsAPIUrlProd;
      const geocodeAPIUrl = isDevMode
        ? API_CONFIG.GEOCODING
        : settings.geocodeAPIUrlProd;
      const apiKeyLocal = isDevMode
        ? settings.apiKey || process.env.REACT_APP_API_KEY || ""
        : "";

      if (!isDevMode) {
        if (!mapsAPIUrl) {
          debug.log(
            "Production mode: No mapsAPIUrlProd provided, using PHP fallback",
          );
          setSuggestions([]);
          setIsSearching(false);
          return;
        }
        try {
          // Use rectangle restriction when regionCorners is available, otherwise fall back to circle
          const locationRestriction = regionCorners
            ? {
                rectangle: {
                  low: {
                    latitude: regionCorners.southwest.lat,
                    longitude: regionCorners.southwest.lng,
                  },
                  high: {
                    latitude: regionCorners.northeast.lat,
                    longitude: regionCorners.northeast.lng,
                  },
                },
              }
            : {
                circle: {
                  center: autocompleteCenter,
                  radius: autocompleteRadius,
                },
              };

          const requestBody = {
            input: value,
            locationRestriction,
          };

          const response = await fetch(mapsAPIUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal,
          });

          if (signal.aborted) return;

          if (!response.ok) {
            throw new Error(
              `PHP Places API error: ${response.status} ${response.statusText}`,
            );
          }

          const data = await response.json();
          if (!data?.suggestions || !Array.isArray(data.suggestions)) {
            setSuggestions([]);
            setIsSearching(false);
            return;
          }

          const suggestionPromises = data.suggestions.map(async (s: any) => {
            const text = s?.placePrediction?.text?.text;
            if (!text) {
              return null;
            }
            if (!geocodeAPIUrl) {
              return { text };
            }
            try {
              const geocodeResponse = await fetch(
                `${geocodeAPIUrl}?address=${encodeURIComponent(text)}`,
                { signal },
              );
              if (signal.aborted) return null;
              const geocodeData = await geocodeResponse.json();

              if (
                geocodeData?.status === "OK" &&
                geocodeData?.results?.length > 0
              ) {
                const { lat, lng } = geocodeData.results[0].geometry.location;
                return { text, location: { lat, lng } };
              }
              return { text };
            } catch (geocodeError) {
              if ((geocodeError as Error).name === "AbortError") return null;
              debug.error(
                "Production mode: Geocode error for suggestion:",
                text,
                geocodeError,
              );
              return { text };
            }
          });

          const suggestions = (await Promise.all(suggestionPromises)).filter(
            (s): s is { text: string; location?: google.maps.LatLngLiteral } =>
              s !== null,
          );

          if (!signal.aborted) {
            setSuggestions(suggestions);
            setIsSearching(false);
          }
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          debug.error("Production mode: Autocomplete error:", error);
          setSuggestions([]);
          setIsSearching(false);
        }
        return;
      }

      // Dev mode: Perform fetch to Google APIs
      try {
        // Use rectangle restriction when regionCorners is available, otherwise fall back to circle
        const locationRestriction = regionCorners
          ? {
              rectangle: {
                low: {
                  latitude: regionCorners.southwest.lat,
                  longitude: regionCorners.southwest.lng,
                },
                high: {
                  latitude: regionCorners.northeast.lat,
                  longitude: regionCorners.northeast.lng,
                },
              },
            }
          : {
              circle: {
                center: autocompleteCenter,
                radius: autocompleteRadius,
              },
            };

        const requestBody = {
          input: value,
          locationRestriction,
        };

        if (!mapsAPIUrl) {
          throw new Error("mapsAPIUrl is undefined");
        }
        const response = await fetch(mapsAPIUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKeyLocal,
          },
          body: JSON.stringify(requestBody),
          signal,
        });

        if (signal.aborted) return;

        if (!response.ok) {
          throw new Error(
            `Places API error: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        if (!data.suggestions) {
          setSuggestions([]);
          setIsSearching(false);
          return;
        }

        const suggestionPromises = data.suggestions.map(async (s: any) => {
          const text = s.placePrediction.text.text;
          if (!geocodeAPIUrl) {
            return { text };
          }
          try {
            const geocodeResponse = await fetch(
              `${geocodeAPIUrl}?address=${encodeURIComponent(
                text,
              )}&key=${apiKeyLocal}`,
              { signal },
            );
            if (signal.aborted) return null;
            const geocodeData = await geocodeResponse.json();

            if (geocodeData.status === "OK" && geocodeData.results.length > 0) {
              const { lat, lng } = geocodeData.results[0].geometry.location;
              return { text, location: { lat, lng } };
            }
            return { text };
          } catch (geocodeError) {
            if ((geocodeError as Error).name === "AbortError") return null;
            debug.error(
              "Dev mode: Geocode error for suggestion:",
              text,
              geocodeError,
            );
            return { text };
          }
        });

        const results = await Promise.all(suggestionPromises);
        if (!signal.aborted) {
          setSuggestions(results.filter((s) => s !== null));
          setIsSearching(false);
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        debug.error("Dev mode: Autocomplete error:", error);
        setSuggestions([]);
        setIsSearching(false);
      }
    },
    [autocompleteCenter, autocompleteRadius, regionCorners],
  );

  // Debounced search - 300ms delay
  const debouncedSearchFrom = useMemo(
    () =>
      debounce((value: string) => {
        // Cancel previous request
        fromAbortRef.current?.abort();
        fromAbortRef.current = new AbortController();
        performSearch(value, "from", fromAbortRef.current.signal);
      }, 300),
    [performSearch],
  );

  const debouncedSearchTo = useMemo(
    () =>
      debounce((value: string) => {
        // Cancel previous request
        toAbortRef.current?.abort();
        toAbortRef.current = new AbortController();
        performSearch(value, "to", toAbortRef.current.signal);
      }, 300),
    [performSearch],
  );

  const handleInputChange = useCallback(
    (value: string, type: "from" | "to") => {
      const onLocationChange =
        type === "from" ? onFromLocationChange : onToLocationChange;
      const setSuggestions =
        type === "from" ? setFromSuggestions : setToSuggestions;
      const setIsDropdownOpen =
        type === "from" ? setIsFromDropdownOpen : setIsToDropdownOpen;
      const setIsSearching =
        type === "from" ? setIsSearchingFrom : setIsSearchingTo;

      if (!value) {
        setSuggestions([]);
        setIsDropdownOpen(false);
        setIsSearching(false);
        onLocationChange(null);
        return;
      }

      // Show dropdown with loading state immediately
      setIsDropdownOpen(true);
      setIsSearching(true);

      // Trigger debounced search
      if (type === "from") {
        debouncedSearchFrom(value);
      } else {
        debouncedSearchTo(value);
      }
    },
    [
      onFromLocationChange,
      onToLocationChange,
      debouncedSearchFrom,
      debouncedSearchTo,
    ],
  );

  const handleBlur = useCallback((type: "from" | "to") => {
    // Delay closing to allow click on suggestion
    setTimeout(() => {
      if (type === "from") {
        setIsFromDropdownOpen(false);
        setIsSearchingFrom(false);
      } else {
        setIsToDropdownOpen(false);
        setIsSearchingTo(false);
      }
    }, 200); // Slightly longer delay for mobile touch
  }, []);

  const handleFocus = useCallback(
    (type: "from" | "to") => {
      const suggestions = type === "from" ? fromSuggestions : toSuggestions;
      if (suggestions.length > 0) {
        if (type === "from") {
          setIsFromDropdownOpen(true);
        } else {
          setIsToDropdownOpen(true);
        }
      }
    },
    [fromSuggestions, toSuggestions],
  );

  const handleSelect = useCallback(
    (index: number, type: "from" | "to") => {
      const suggestions = type === "from" ? fromSuggestions : toSuggestions;
      const inputRef = type === "from" ? fromInputRef : toInputRef;
      const onLocationChange =
        type === "from" ? onFromLocationChange : onToLocationChange;
      const setIsDropdownOpen =
        type === "from" ? setIsFromDropdownOpen : setIsToDropdownOpen;
      const setHighlightedIndex =
        type === "from" ? setFromHighlightedIndex : setToHighlightedIndex;

      if (index >= 0 && index < suggestions.length && inputRef.current) {
        const suggestion = suggestions[index];
        inputRef.current.value = suggestion.text;
        onLocationChange(suggestion.location ?? null, suggestion.text);
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        // Blur input after selection on mobile to hide keyboard
        inputRef.current.blur();
      }
    },
    [fromSuggestions, toSuggestions, onFromLocationChange, onToLocationChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, type: "from" | "to") => {
      const suggestions = type === "from" ? fromSuggestions : toSuggestions;
      const highlightedIndex =
        type === "from" ? fromHighlightedIndex : toHighlightedIndex;
      const setHighlightedIndex =
        type === "from" ? setFromHighlightedIndex : setToHighlightedIndex;
      const isDropdownOpen =
        type === "from" ? isFromDropdownOpen : isToDropdownOpen;

      if (e.key === "Escape") {
        if (type === "from") {
          setIsFromDropdownOpen(false);
          setIsSearchingFrom(false);
          setFromHighlightedIndex(-1);
        } else {
          setIsToDropdownOpen(false);
          setIsSearchingTo(false);
          setToHighlightedIndex(-1);
        }
        return;
      }

      if (!isDropdownOpen || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex =
          highlightedIndex < suggestions.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(nextIndex);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevIndex =
          highlightedIndex > 0 ? highlightedIndex - 1 : suggestions.length - 1;
        setHighlightedIndex(prevIndex);
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        handleSelect(highlightedIndex, type);
      }
    },
    [
      fromSuggestions,
      toSuggestions,
      fromHighlightedIndex,
      toHighlightedIndex,
      isFromDropdownOpen,
      isToDropdownOpen,
      handleSelect,
    ],
  );

  const handleUseMyLocation = useCallback(
    async (type: "from" | "to") => {
      if (!navigator.geolocation) return;

      const inputRef = type === "from" ? fromInputRef : toInputRef;
      const setIsLoading = type === "from" ? setIsLoadingFrom : setIsLoadingTo;
      const setSuggestions =
        type === "from" ? setFromSuggestions : setToSuggestions;
      const onLocationChange =
        type === "from" ? onFromLocationChange : onToLocationChange;
      const setIsDropdownOpen =
        type === "from" ? setIsFromDropdownOpen : setIsToDropdownOpen;

      setIsLoading(true);
      setIsDropdownOpen(false);
      try {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          },
        );

        const { latitude, longitude } = position.coords;

        if (process.env.NODE_ENV !== "development") {
          const settings: TrpSettings = window.trpSettings || {};
          const geocodeAPIUrl = settings.geocodeAPIUrlProd;
          if (!geocodeAPIUrl) {
            debug.log(
              "Production mode: No geocodeAPIUrlProd provided, using coordinates only",
            );
            if (inputRef.current) {
              inputRef.current.value = "Geocoding unavailable";
            }
            setSuggestions([]);
            onLocationChange({ lat: latitude, lng: longitude });
            return;
          }
          try {
            const geocodeResponse = await fetch(
              `${geocodeAPIUrl}?latlng=${latitude},${longitude}`,
            );
            const geocodeData = await geocodeResponse.json();

            if (
              geocodeData?.status === "OK" &&
              geocodeData?.results?.length > 0
            ) {
              const address = geocodeData.results[0].formatted_address;
              if (inputRef.current) {
                inputRef.current.value = address;
                setSuggestions([]);
                onLocationChange({ lat: latitude, lng: longitude }, address);
              }
            } else {
              debug.warn("Production mode: Invalid geocode response");
              if (inputRef.current) {
                inputRef.current.value = "Address not found";
              }
              setSuggestions([]);
              onLocationChange({ lat: latitude, lng: longitude });
            }
          } catch (geocodeError) {
            debug.error("Production mode: Geocode error:", geocodeError);
            if (inputRef.current) {
              inputRef.current.value = "Error fetching address";
            }
            setSuggestions([]);
            onLocationChange({ lat: latitude, lng: longitude });
          }
          return;
        }

        // Dev mode: Perform fetch to Google Geocoding API
        const geocodeResponse = await fetch(
          `${API_CONFIG.GEOCODING}?latlng=${latitude},${longitude}&key=${apiKey}`,
        );
        const geocodeData = await geocodeResponse.json();

        if (geocodeData.status === "OK" && geocodeData.results.length > 0) {
          const address = geocodeData.results[0].formatted_address;
          if (inputRef.current) {
            inputRef.current.value = address;
            setSuggestions([]);
            onLocationChange({ lat: latitude, lng: longitude }, address);
          }
        } else if (inputRef.current) {
          inputRef.current.value = "Address not found";
          setSuggestions([]);
          onLocationChange({ lat: latitude, lng: longitude });
        }
      } catch (error) {
        debug.error("Dev mode: Location fetch error:", error);
        if (inputRef.current) {
          inputRef.current.value = "Error fetching location";
        }
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [apiKey, onFromLocationChange, onToLocationChange],
  );

  const handleClearInput = useCallback(
    (type: "from" | "to") => {
      const inputRef = type === "from" ? fromInputRef : toInputRef;
      const setSuggestions =
        type === "from" ? setFromSuggestions : setToSuggestions;
      const onLocationChange =
        type === "from" ? onFromLocationChange : onToLocationChange;
      const setIsDropdownOpen =
        type === "from" ? setIsFromDropdownOpen : setIsToDropdownOpen;

      if (inputRef.current) {
        inputRef.current.value = "";
        setSuggestions([]);
        setIsDropdownOpen(false);
        onLocationChange(null);
      }
    },
    [onFromLocationChange, onToLocationChange],
  );

  return (
    <>
      <LocationInput
        type="from"
        inputRef={fromInputRef}
        suggestions={fromSuggestions}
        isLoading={isLoadingFrom}
        isSearching={isSearchingFrom}
        isDropdownOpen={isFromDropdownOpen}
        highlightedIndex={fromHighlightedIndex}
        onInputChange={(value) => handleInputChange(value, "from")}
        onClear={() => handleClearInput("from")}
        onUseMyLocation={() => handleUseMyLocation("from")}
        onLocationChange={onFromLocationChange}
        onBlur={() => handleBlur("from")}
        onFocus={() => handleFocus("from")}
        onKeyDown={(e) => handleKeyDown(e, "from")}
        onHighlight={(index) => setFromHighlightedIndex(index)}
        onSelect={(index) => handleSelect(index, "from")}
      />
      <LocationInput
        type="to"
        inputRef={toInputRef}
        suggestions={toSuggestions}
        isLoading={isLoadingTo}
        isSearching={isSearchingTo}
        isDropdownOpen={isToDropdownOpen}
        highlightedIndex={toHighlightedIndex}
        onInputChange={(value) => handleInputChange(value, "to")}
        onClear={() => handleClearInput("to")}
        onUseMyLocation={() => handleUseMyLocation("to")}
        onLocationChange={onToLocationChange}
        onBlur={() => handleBlur("to")}
        onFocus={() => handleFocus("to")}
        onKeyDown={(e) => handleKeyDown(e, "to")}
        onHighlight={(index) => setToHighlightedIndex(index)}
        onSelect={(index) => handleSelect(index, "to")}
      />
      {/* Date and Time side by side for mobile */}
      <div className="trp-mobile-datetime-row">
        <div className="input-group">
          <div className="content-column">
            <div className="input-wrapper">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
                className="input"
                aria-label="Departure date"
              />
            </div>
          </div>
        </div>
        <div className="input-group">
          <div className="content-column">
            <div className="input-wrapper">
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => onTimeChange(e.target.value)}
                className="input"
                aria-label="Departure time"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InputGroupMobile;
